import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import {
  BarChart,
  Briefcase,
  Cpu,
  ExternalLink,
  Gift,
  Home,
  Icon,
  Package,
  Tv,
  UploadCloud,
  User,
  Users,
} from "react-feather";
import { useGetGuestViewStatusQuery } from "@lib/store/slices/guestView";
import Sidebar from "../Various/Sidebar/Sidebar";
import SidebarButton from "../Various/Sidebar/SidebarButton";
import SidebarSection from "../Various/Sidebar/SidebarSection";
import useSubscriptionLimits from "@lib/hooks/subscription/useSubscriptionLimits";

type AccountPage = {
  label: string;
  icon: Icon;
  href: string;
};
type AccountPagesList = {
  [key: string]: AccountPage[];
};

export const ACCOUNT_PAGES = {
  Jobs: [
    { label: "Jobs", href: "/account/jobs", icon: Briefcase },
    { label: "Performance", href: "/account/performance", icon: BarChart },
    { label: "Unternehmen", href: "/account/companies", icon: Home },
    {
      label: "Job-Import",
      href: "/account/import",
      icon: UploadCloud,
    },
  ],
  Konto: [
    { label: "Abonnement", href: "/account/subscription", icon: Package },
    { label: "AI Credits", href: "/account/ai-credits", icon: Cpu },
    { label: "Konto", href: "/account/profile", icon: User },
  ],
};

function AccountAreaSidebar() {
  const router = useRouter();
  const { data: guestViewStatus } = useGetGuestViewStatusQuery();
  const planLimits = useSubscriptionLimits();
  const canUseApiTokens =
    planLimits?.subscription?.name.startsWith("professional");

  const pages: AccountPagesList = { ...ACCOUNT_PAGES };

  const canUseGuestViewMenu =
    guestViewStatus?.canUseView || guestViewStatus?.isInView;
  if (canUseGuestViewMenu) {
    pages.Administration = [
      {
        label: "Impersonate",
        href: "/account/impersonate",
        icon: Users,
      },
      {
        label: "Gutscheine",
        href: "/account/coupons",
        icon: Gift,
      },
      {
        label: "Telescope",
        href: `${process.env.NEXT_PUBLIC_BACKEND_URL?.replace(
          "/api/v1",
          ""
        )}/telescope`,
        icon: Tv,
      },
    ];
  }

  if (canUseApiTokens) {
    pages.API = [
      {
        label: "API Tokens",
        href: "/account/api-tokens",
        icon: Package,
      },
    ];
  }

  return (
    <Sidebar>
      {Object.entries(pages).map(([sectionTitle, sectionPages]) => (
        <SidebarSection key={sectionTitle} title={sectionTitle}>
          {sectionPages.map((link) => {
            const isActive = link.href === router.pathname;
            const isExternal = link.href.startsWith("http");

            const buttonElement = (
              <SidebarButton
                key={link.label}
                elementType="a"
                isActive={isActive}
                icon={link.icon}
                className="flex items-center"
              >
                {link.label}
                {isExternal && <ExternalLink size={12} className="ml-2" />}
              </SidebarButton>
            );

            if (isExternal) {
              return (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  key={link.href}
                >
                  {buttonElement}
                </a>
              );
            }

            return (
              <Link href={link.href} passHref key={link.href} legacyBehavior>
                {buttonElement}
              </Link>
            );
          })}
        </SidebarSection>
      ))}
    </Sidebar>
  );
}

export default AccountAreaSidebar;
